###
Advanced Filter Configuration:

[
	{
		"title": "category.of.translation", // Translation key to use. [Optional]
		"filters": {
			"name_of_filter": {
				"property": "some>property",
				"label": "category.of.translation", // Translation key to use
				"type": "text|select|boolean|entity-type", // Default "text". [Optional]
				"comparator": "any-of-api-comparators", // Default "LIKE" or type dependant. [Optional]
				"options": {}, // Type dependant options to use. [Optional]
				"value": "", // Default value to be applied. [Optional]
			},
			"another_filter": {...}
		}
	},
	{...}
]

Short version: just object of filters:

{
	"name_of_filter": {
		"property": "some>property",
		"label": "category.of.translation", // Translation key to use
		"type": "text|select|boolean|entity-type", // Default "text". [Optional]
		"comparator": "any-of-api-comparators", // Default "LIKE" or type dependant. [Optional]
		"options": {}, // Type dependant options to use. All types excepts `with` for query formation. [Optional]
		"value": "", // Default value to be applied. [Optional]
	},
	"another_filter": {...}
}

Types options:

	* [Any]
		* `with` for query formation
		* `skip` to skip filter rendering.
		* `skip_query` only for rendering and will not be auto applied to query.
		* `additional_conditions` array of additional conditions. Each array element contains following properties:
			** `property`: string - required, same as base filter
			** `comparator`: string - optional, default `LIKE`
			** `value`: mixed - optional, default same as base filter
		* `options`
			** `translatable`: array - Add additional search conditions to match translated property
				*** `property`: string - label of translation to use
				*** `relation`: string - optional, default "translations". Specify where translations are located in relation tree

	* "entity-type":
		* label_prefix: string - Translation key prefix to replaced with Entity Type name
		* entity_type: string - Entity Type constant name to be used
		* exclude: array - List of Entity Labels to exclude from selection. [Optional]

  * "date"
		* options: Check DateRangePicker configuration options

	* "select"
		If value callback returns NULL, than fallback to text type. If so, use options.alt_property as property
		E.g. {property: 'cat_id', options: {alt_property: 'cat_name'}} - Use `cat_name` as filter instead of searching by ID
		* options:
			** skip_on_empty: boolean - Skip filter if callback returns NULL
			** alt_property: string - Use this property instead if callback returns NULL
			** key_property: string - Use this property as key. Default `id`
			** text_property: string - Use this property as text. Default `name`
			** group_property: string - Use this property as group

###
class AdvancedSearchService extends BaseService
	@register()
	@inject '$q',
		'$injector'
		'translate'
		'LanguageService'
		'SEARCH_FILTER_TYPE'

	###
	@param {Query} query

  @returns {(null|Criteria)} Formatted criteria or NULL if query should be skipped
	###
	apply: (query, configuration) ->
		applyBaseCondition = (filter) =>
			if !filter.value? || filter.value == '' || filter.options?.skip == true || filter.options?.skip_query == true
				return null

			comparator = filter.comparator

			if filter.options.with
				query.with filter.options.with...

			if filter.value == '-1'
				return query.criteria filter.property, 'IS', 'NULL'

			if comparator
				return query.criteria filter.property, comparator, filter.value

			if filter.type == @SEARCH_FILTER_TYPE.date
				if !filter.value.start? || !filter.value.end?
					return null

				criteria = query.criteria filter.property, '>=', filter.value.start
				criteria.and filter.property, '<=', filter.value.end

				return criteria

			comparator = switch filter.type
				when @SEARCH_FILTER_TYPE.boolean, @SEARCH_FILTER_TYPE.entityType, @SEARCH_FILTER_TYPE.select then '='
				else 'LIKE'

			criteria = query.criteria filter.property, comparator, filter.value

			if filter.options?.translatable?
				applyTranslatableConditions filter, query

			return criteria

		applyTranslatableConditions = (filter, query) =>
			filter.options.translatable.relation ?= 'translations'
			relationName = filter.options.translatable.relation
			relation = relationName.replace(/[.>]/, '_')

			query
				# Ensure there is "with something", otherwise data will be duplicated
				.with relationName
				.criteria "#{relation}>value", 'LIKE', filter.value, 'OR'
				.and "#{relation}>label", '=', filter.options.translatable.property
				.and "#{relation}>language_id", '=', @LanguageService.getDisplayLanguage().id

		applyAdditionalConditions = (criteria, conditions, value) =>
			for condition in conditions
				if !condition.value? && value == '-1'
					condition.comparator = 'IS'
					condition.value = 'NULL'
				else
					condition.comparator ?= 'LIKE'
					condition.value ?= value

				condition.logical ?= 'OR'

				criteria.add condition.property, condition.comparator, condition.value, condition.logical

		for configurationBlock in configuration.configuration
			for filterKey, filter of configurationBlock.filters
				filter = angular.copy(filter)

				criteria = applyBaseCondition(filter)

				if !criteria? || _.isEmpty(filter.options.additional_conditions)
					continue

				applyAdditionalConditions(criteria, filter.options.additional_conditions, filter.value)

	###
	@param valueCallback - Used for `select` type. Must return promise that resolves
		to array of objects with `id` and `name` fields or NULL if no values provided.
		If NULL is returned, than field is treated as `text` type
	###
	initializeFilters: (configuration, valueCallback) ->
		if !configuration.configuration[0]?
			configuration.configuration = [{filters: configuration.configuration}]

		configuration.valid = true
		configuration.pristine = true

		for configurationBlock in configuration.configuration
			configurationBlock.expanded = true

			for filterKey, filter of configurationBlock.filters
				filter.key ?= filterKey
				filter.value ?= null
				filter.options ?=
					skip: false

				switch filter.type
					when @SEARCH_FILTER_TYPE.boolean then @_initializeBooleanFilter(filterKey, filter)
					when @SEARCH_FILTER_TYPE.select then @_initializeSelectFilter(filterKey, filter, valueCallback)
					when @SEARCH_FILTER_TYPE.entityType then @_initializeEntityTypeFilter(filterKey, filter)
					when @SEARCH_FILTER_TYPE.date then @_initializeDateFilter(filterKey, filter)
					else @_initializeTextFilter(filterKey, filter)

		@originalConfiguration = angular.copy(configuration.configuration)

		return configuration

	reset: (configuration) ->
		configuration.pristine = true
		configuration.valid = true

		for configurationBlock, i in @originalConfiguration
			for filterKey, filter of configurationBlock.filters
				configuration.configuration[i].filters[filterKey].value = filter.value

	onChange: (configuration) ->
		for configurationBlock, i in @originalConfiguration
			for filterKey, filter of configurationBlock.filters
				if configuration.configuration[i].filters[filterKey].value != filter.value
					configuration.pristine = false

					return

		configuration.pristine = true

	_initializeTextFilter: (filterKey, filter) ->
		# Just in case there is some other type with fallback to text
		filter.type = @SEARCH_FILTER_TYPE.text
	_initializeBooleanFilter: (filterKey, filter) ->
		# Does nothing
	_initializeDateFilter: (filterKey, filter) ->
		filter.options.timePicker ?= true
	_initializeSelectFilter: (filterKey, filter, valueCallback) ->
		filter.options.skip_on_empty ?= false
		filter.options.key_property ?= 'id'
		filter.options.text_property ?= 'name'

		if filter.options.values? || filter.options.repository
			return

		valueCallback ?= @$q.reject()
		valuePromise = valueCallback(filterKey)
		valuePromise ?= @$q.reject()

		valuePromise
			.then (values) =>
				if values?
					filter.options.values = values
			.finally =>
				if filter.options.values?
					return

				if filter.options.alt_property
					filter.property = filter.options.alt_property

				if filter.options.alt_label
					filter.label = filter.options.alt_label

				if filter.options.skip_on_empty != true
					@_initializeTextFilter(filterKey, filter)
				else
					filter.options.skip = true

	_initializeEntityTypeFilter: (filterKey, filter) ->
		if !@$injector.has(filter.options.entity_type)
			throw new Error("Entity Type constant #{filter.options.entity_type} does not exist")

		filter.property ?= 'entity_type'
		filter.label ?= 'general.type'

		entityTypes = []

		for key, entityType of @$injector.get(filter.options.entity_type)
			if filter.options.except? && filter.options.except.includes(entityType)
				continue

			entityTypes.push({
				entity_type: entityType
				label: @translate(filter.options.label_prefix + '.' + entityType.toLowerCase())
			})

		filter.options.values = entityTypes
